import { Trash2, MapPin, Car, Truck, Bus, Bike, Package, Radio, Square, Clock, RefreshCw, Share2, UserSquare2 } from "lucide-react";

function timeAgo(isoString) {
  if (!isoString) return null;
  const diff = Date.now() - new Date(isoString).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)   return "just now";
  if (mins  < 60)  return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  return `${days}d ago`;
}

const TYPE_ICONS = { Car, Truck, Bus, "Two-Wheeler":Bike, Other:Package };

const STATUS = {
  Active:  { color:"#16A34A", dot:"#16A34A",  label:"Live"    },
  Idle:    { color:"#D97706", dot:"#F59E0B",   label:"Idle"    },
  Offline: { color:"#9CA3AF", dot:"#D1D5DB",   label:"Offline" },
};

export default function VehicleCard({ vehicle, isSelected, onClick, onRemove, onTrack, onStopTrack, onRefresh, onShare, onViewProfile }) {
  const s    = STATUS[vehicle.status] || STATUS.Offline;
  const Icon = TYPE_ICONS[vehicle.type] || Package;

  return (
    <div
      onClick={onClick}
      style={{
        padding: "16px 16px",
        borderRadius: 12,
        cursor: "pointer",
        background: isSelected ? "#EEF2FF" : "#ffffff",
        border: `1.5px solid ${isSelected ? "#C7D2FE" : "#EBEBEB"}`,
        boxShadow: isSelected
          ? "0 0 0 3px rgba(99,120,220,0.1)"
          : "0 1px 2px rgba(0,0,0,0.04)",
        transition: "all 0.15s ease",
      }}
      onMouseOver={e => {
        if (!isSelected) {
          e.currentTarget.style.background = "#F9FAFB";
          e.currentTarget.style.borderColor = "#D1D5DB";
        }
      }}
      onMouseOut={e => {
        if (!isSelected) {
          e.currentTarget.style.background = "#ffffff";
          e.currentTarget.style.borderColor = "#EBEBEB";
        }
      }}
    >
      {/* Row 1: Plate + status dot + delete */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:9 }}>
        <span style={{
          fontFamily:"'SF Mono','Fira Code',monospace",
          fontSize:11, fontWeight:700, letterSpacing:"0.06em",
          color:"#374151", background:"#F7F8F9",
          border:"1px solid #EBEBED", borderRadius:5,
          padding:"2px 8px", whiteSpace:"nowrap",
        }}>{vehicle.plateNumber}</span>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Status dot only — no badge */}
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <div style={{
              width:6, height:6, borderRadius:"50%",
              background: s.dot,
              boxShadow: vehicle.isTracking ? `0 0 0 3px ${s.dot}22` : "none",
            }} />
            <span style={{ fontSize:12, fontWeight:600, color:s.color, letterSpacing:0.2 }}>
              {vehicle.isTracking ? "LIVE" : s.label}
            </span>
          </div>
          {onViewProfile && (
            <button
              onClick={e => { e.stopPropagation(); onViewProfile(vehicle); }}
              title="View vehicle profile"
              style={{
                background:"none", border:"none", padding:"3px 4px", cursor:"pointer",
                color:"#D1D5DB", borderRadius:5, display:"flex", lineHeight:0,
                transition:"color 0.15s",
              }}
              onMouseOver={e => e.currentTarget.style.color="#4F46E5"}
              onMouseOut={e  => e.currentTarget.style.color="#D1D5DB"}
            ><UserSquare2 size={14} /></button>
          )}
          {onShare && (
            <button
              onClick={e => { e.stopPropagation(); onShare(vehicle.id); }}
              title="Share vehicle tracking"
              style={{
                background:"none", border:"none", padding:"3px 4px", cursor:"pointer",
                color:"#D1D5DB", borderRadius:5, display:"flex", lineHeight:0,
                transition:"color 0.15s",
              }}
              onMouseOver={e => e.currentTarget.style.color="#4F46E5"}
              onMouseOut={e  => e.currentTarget.style.color="#D1D5DB"}
            ><Share2 size={14} /></button>
          )}
          <button
            onClick={e => { e.stopPropagation(); onRemove(vehicle.id); }}
            style={{
              background:"none", border:"none", padding:"3px 4px", cursor:"pointer",
              color:"#D1D5DB", borderRadius:5, display:"flex", lineHeight:0,
              transition:"color 0.15s",
            }}
            onMouseOver={e => e.currentTarget.style.color="#EF4444"}
            onMouseOut={e  => e.currentTarget.style.color="#D1D5DB"}
          ><Trash2 size={14} /></button>
        </div>
      </div>

      {/* Row 2: Name + type */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
        <Icon size={14} color="#9CA3AF" style={{ flexShrink:0 }} />
        <span style={{
          fontSize:15, fontWeight:600, letterSpacing:-0.1,
          color: isSelected ? "#4F46E5" : "#111827",
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1,
        }}>
          {vehicle.nickname}
        </span>
        <span style={{ fontSize:12, color:"#B0B7C3", flexShrink:0, fontWeight:500 }}>{vehicle.type}</span>
      </div>

      {/* Row 3: Location */}
      <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:7 }}>
        <MapPin size={12} color="#B0B7C3" style={{ flexShrink:0 }} />
        <span style={{
          fontSize:13, color:"#6B7280",
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1,
        }}>
          {vehicle.location || "Location unavailable"}
        </span>
        {vehicle.lastUpdated && !vehicle.isTracking && (
          <span style={{ fontSize:12, color:"#C4C9D4", flexShrink:0 }}>
            {timeAgo(vehicle.lastUpdated)}
          </span>
        )}
      </div>

      {/* No data warning */}
      {vehicle.noRecentData && (
        <div style={{
          display:"flex", alignItems:"center", gap:6, marginBottom:7,
          background:"#FFF9EC", border:"1px solid #FDE68A",
          borderRadius:7, padding:"4px 9px",
        }}>
          <Clock size={11} color="#D97706" style={{ flexShrink:0 }} />
          <span style={{ fontSize:12, color:"#92400E", fontWeight:500, flex:1 }}>
            No toll data in last 7 days
          </span>
          {onRefresh && (
            <button
              onClick={e => { e.stopPropagation(); onRefresh(vehicle.id); }}
              style={{
                background:"none", border:"none", padding:"1px 0",
                cursor:"pointer", display:"flex", alignItems:"center", gap:3,
                fontSize:12, fontWeight:600, color:"#D97706",
                transition:"color 0.15s",
              }}
              onMouseOver={e => e.currentTarget.style.color="#B45309"}
              onMouseOut={e  => e.currentTarget.style.color="#D97706"}
            >
              <RefreshCw size={11} /> Check
            </button>
          )}
        </div>
      )}

      {/* Last toll chip */}
      {!vehicle.noRecentData && !vehicle.isTracking && vehicle.tollHistory?.length > 0 && (() => {
        const t = vehicle.tollHistory[0];
        return (
          <div style={{
            display:"flex", alignItems:"center", gap:5, marginBottom:7,
            background:"#EEF2FF", border:"1px solid #C7D2FE",
            borderRadius:7, padding:"4px 9px",
          }}>
            <Clock size={11} color="#4F6EF7" style={{ flexShrink:0 }} />
            <span style={{
              fontSize:12, color:"#3B5BDB", fontWeight:500,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1,
            }}>
              {t.plaza}
            </span>
            <span style={{ fontSize:12, color:"#93A8F4", flexShrink:0 }}>
              {timeAgo(t.timestamp)}
            </span>
          </div>
        );
      })()}

      {/* Track / Live row */}
      {vehicle.isTracking ? (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{
            display:"flex", alignItems:"center", gap:6,
            background:"#ECFDF5", border:"1px solid #A7F3D0",
            borderRadius:7, padding:"5px 10px",
          }}>
            <div style={{
              width:6, height:6, borderRadius:"50%", background:"#10B981",
              animation:"pulse 1.5s ease-in-out infinite",
            }} />
            <span style={{ fontSize:13, fontWeight:700, color:"#059669", letterSpacing:0.5 }}>LIVE</span>
          </div>
          {onStopTrack && (
            <button
              onClick={e => { e.stopPropagation(); onStopTrack(vehicle.id); }}
              style={{
                display:"flex", alignItems:"center", gap:4,
                background:"none", border:"1px solid #EBEBEB", borderRadius:7,
                padding:"5px 10px", fontSize:13, fontWeight:500, color:"#9CA3AF",
                cursor:"pointer", transition:"all 0.15s",
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor="#FECACA"; e.currentTarget.style.color="#EF4444"; }}
              onMouseOut={e  => { e.currentTarget.style.borderColor="#EBEBEB"; e.currentTarget.style.color="#9CA3AF"; }}
            >
              <Square size={9} /> Stop
            </button>
          )}
        </div>
      ) : onTrack && (
        <button
          onClick={e => { e.stopPropagation(); onTrack(vehicle.id); }}
          style={{
            width:"100%", padding:"11px 0",
            background:"#111827", border:"none", borderRadius:8,
            color:"#fff", fontSize:14, fontWeight:600, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:6,
            transition:"background 0.15s",
          }}
          onMouseOver={e => e.currentTarget.style.background="#1F2937"}
          onMouseOut={e  => e.currentTarget.style.background="#111827"}
        >
          <Radio size={13} /> Track
        </button>
      )}
    </div>
  );
}
